<script setup lang="ts">
import InputCardComponent from '@/components/InputCardComponent.vue';
import SliderInputComponent from '@/components/SliderInputComponent.vue';
import MileageNudgeComponent from '@/components/MileageNudgeComponent.vue';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { addYears, eachYearOfInterval, endOfMonth, isWithinInterval, startOfMonth, subYears } from 'date-fns';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import { calculateMaxYearlyMileage, getMileageTier, getMonthName } from '@/helpers';
import { useMileageValidation } from '@/composables/useMileageValidation';

const agreementStore = useAgreementStore()
const { agreement, agreementType } = storeToRefs(useAgreementStore())
const { config, defaults } = storeToRefs(useConfigStore())

const serviceDateMin = ref<Date>(startOfMonth(subYears(new Date(), 2)))
const serviceDateMax = ref<Date>(endOfMonth(addYears(new Date(), 2)))
const monthPickerLabel = computed(() => {
  return agreement.value.nextServicePaymentMethod === 'include' ? 'Hvornår var seneste service?' : 'Hvornår er næste service?'
})

const mileageTier = computed(() => getMileageTier(agreement.value.registrationYear, agreement.value.registrationMonth))

function isValidMonth(index: number): boolean {
  const { startYear } = agreement.value

  if (!startYear) return false

  const interval = {
    start: serviceDateMin.value,
    end: serviceDateMax.value
  }

  const monthToCheck = new Date(startYear, index, 1)

  return isWithinInterval(monthToCheck, interval)
}

watch(() => agreement.value.nextServicePaymentMethod, (value) => {
  if (value) {
    switch (value) {
      case 'include':
        serviceDateMin.value = startOfMonth(subYears(new Date(), 2))
        serviceDateMax.value = endOfMonth(new Date())
        break;

      case 'cash':
        serviceDateMin.value = startOfMonth(new Date())
        serviceDateMax.value = endOfMonth(addYears(new Date(), 2))
        break;

      default:
        break;
    }
  }
}, { immediate: true })

function onPaymentMethodChange() {
  agreement.value.startYear = undefined
  agreement.value.startMonth = undefined
}

const monthsSinceService = computed(() => {
  const { startYear, startMonth } = agreement.value

  if (startYear == null || startMonth == null) return undefined

  const now = new Date()
  return Math.max((now.getFullYear() - startYear) * 12 + (now.getMonth() - startMonth), 1)
})

const maxAnnualAllowed = computed(() => calculateMaxYearlyMileage(agreementType.value, defaults.value, agreement.value.oldMileage, agreement.value.duration))

const { result: mileageResult, dismiss: dismissNudge, resetDismiss } = useMileageValidation({
  kmAtService: computed(() => agreement.value.kmAtService),
  currentKm: computed(() => agreement.value.oldMileage),
  monthsSinceService,
  annualKm: computed(() => agreement.value.yearlyMileage),
  maxAnnualAllowed,
  kmMax: computed(() => defaults.value.maxYearlyMileage),
})

watch([
  () => agreement.value.yearlyMileage,
  () => agreement.value.kmAtService,
  () => agreement.value.oldMileage,
  () => agreement.value.startYear,
  () => agreement.value.startMonth,
], resetDismiss)

// The current-mileage slider starts on the page-1 estimate, which can easily be lower
// than a kmAtService the user types in — without this, that's a hard-error dead end
// (cur < svc) until they happen to also drag the slider up. Lift the floor instead.
watch(() => agreement.value.kmAtService, (value) => {
  if (value != null && (agreement.value.oldMileage == null || agreement.value.oldMileage < value)) {
    agreement.value.oldMileage = value
    agreementStore.calculate()
  }
})

function onAdjustAnnualKm(value: number) {
  agreement.value.yearlyMileage = value
  agreementStore.calculate()
}
</script>

<template>
  <v-container>
    <v-row>
      <v-col>
        <InputCardComponent label="Fra hvornår gælder aftalen?">
          <v-radio-group v-model="agreement.nextServicePaymentMethod" @update:model-value="onPaymentMethodChange" hide-details>
            <v-radio v-if="mileageTier !== 'thisMonth'" label="Fra sidste service- eller leveringstidspunkt." value="include"></v-radio>
            <v-radio label="Fra et fremtidigt service- eller leveringstidspunkt" value="cash"></v-radio>
          </v-radio-group>
        </InputCardComponent>
      </v-col>
    </v-row>
    <v-slide-x-transition>
      <v-row v-if="agreement.nextServicePaymentMethod">
        <v-col cols="12" md="6">
          <InputCardComponent
            :label="monthPickerLabel"
          >
            <v-row>
              <v-col>
                <v-btn-toggle
                  v-model="agreement.startYear"
                  class="w-100"
                  variant="flat"
                >
                  <v-btn
                    v-for="year in eachYearOfInterval({ start: serviceDateMin, end: serviceDateMax })"
                    :value="year.getFullYear()"
                    class="flex-grow-1"
                  >
                    {{ year.getFullYear() }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-slide-y-transition>
              <v-row v-show="agreement.startYear">
                <v-col>
                  <v-row no-gutters>
                    <v-col
                      v-for="n in 12"
                      :key="n"
                      cols="4"
                    >
                      <v-btn
                        class="w-100 text-capitalize"
                        variant="text"
                        :disabled="!isValidMonth(n - 1)"
                        :active="agreement.startMonth === n - 1"
                        @click="agreement.startMonth = n - 1"
                      >
                        {{ getMonthName(n - 1) }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-slide-y-transition>
          </InputCardComponent>
        </v-col>
        <v-col v-if="mileageTier !== 'thisMonth'" cols="12" md="6">
          <InputCardComponent label="Kilometerstand">
            <v-text-field
              v-if="mileageTier === 'older' && agreement.nextServicePaymentMethod === 'include'"
              v-model.number="agreement.kmAtService"
              type="number"
              label="Kilometerstand ved seneste service"
              class="mb-4"
              hide-details
            />
            <SliderInputComponent
              v-model="agreement.oldMileage"
              label="Hvor langt har dit køretøj kørt nu?"
              :min="0"
              :max="config?.maxStartMileage"
              :step="5000"
              unit="km"
              @update:model-value="agreement.mileageTouched = true"
            />
            <MileageNudgeComponent
              :result="mileageResult"
              @adjust="onAdjustAnnualKm"
              @dismiss="dismissNudge"
            />
          </InputCardComponent>
        </v-col>
      </v-row>
    </v-slide-x-transition>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/details" />
    <NextComponent
      to="/agreement/addons"
      :disabled="!agreement.startYear || typeof agreement.startMonth !== 'number' ||
        (mileageTier !== 'thisMonth' && typeof agreement.oldMileage !== 'number') ||
        (mileageTier === 'older' && agreement.nextServicePaymentMethod === 'include' && !agreement.kmAtService) ||
        mileageResult.type === 'error'"
    />
  </BottomBarComponent>
</template>

<style>
.month-wrapper {
  display: flex;
  grid-template-columns: repeat(1fr, 4);
}

.month-button {

}
</style>
